import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class ClevertapService {
  private isCleverTapAvailable = false;
  private lastProfileSignature = "";
  // Holds the dynamically-imported CleverTap instance (null until init() succeeds on non-iOS)
  private ct: typeof import("clevertap-web-sdk").default | null = null;
  // Ensures the localStorage.setItem runtime guard is only installed once per page load
  private static _storageGuardInstalled = false;

  private sanitizePayload(payload?: Record<string, any>): Record<string, any> {
    if (!payload) return {};
    const cleaned: Record<string, any> = {};
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      const isNullish = value === null || value === undefined;
      const isEmptyString = typeof value === "string" && value.trim() === "";
      if (!isNullish && !isEmptyString) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  private normalizeString(value: unknown): string {
    return typeof value === "string" ? value.trim() : String(value ?? "").trim();
  }

  private isInvalidValue(value: unknown): boolean {
    const normalized = this.normalizeString(value).toLowerCase();
    return !normalized || normalized === "null" || normalized === "undefined" || normalized === "nan";
  }

  private normalizeEmail(value: unknown): string | undefined {
    if (this.isInvalidValue(value)) return undefined;
    return this.normalizeString(value).toLowerCase();
  }

  private normalizePhone(value: unknown): string | undefined {
    if (this.isInvalidValue(value)) return undefined;
    const raw = this.normalizeString(value).replace(/\s+/g, "");
    const normalized = raw.startsWith("+") ? raw : `+${raw}`;
    return /^\+[0-9]{6,15}$/.test(normalized) ? normalized : undefined;
  }

  private isTikTokBrowser(): boolean {
    try {
      const userAgent = navigator.userAgent || "";
      return (
        userAgent.includes("TikTok") ||
        userAgent.includes("BytedanceWebview") ||
        userAgent.includes("musical_ly")
      );
    } catch {
      return false;
    }
  }

  private isUCBrowser(): boolean {
    try {
      const userAgent = navigator.userAgent || "";
      return userAgent.includes("UCBrowser") || userAgent.includes("UCWEB");
    } catch {
      return false;
    }
  }

  private isAffectedByInAppsRecursionBug(): boolean {
    try {
      const userAgent = navigator.userAgent || "";
      // iOS 18.6.2+ with Chrome 397.0+ has infinite recursion bug in CleverTap findTopmostVisibleElement
      const hasIOS186 = userAgent.includes("iOS 18.6.2") || userAgent.match(/OS 18_6_[2-9]|OS 18_[7-9]/);
      const hasChrome397Plus = userAgent.match(/Chrome\/(3[0-9]{2}|[4-9][0-9]{2}|[0-9]{4})/);
      return !!(hasIOS186 && hasChrome397Plus);
   } catch {
      return false;
	  }
  }

  private isIOSDevice(): boolean {
    try {
      const userAgent = navigator.userAgent || "";
      return /iPad|iPhone|iPod/.test(userAgent) && !userAgent.includes("Windows");
    } catch {
      return false;
    }
  }

  private isChromeOnIOS(): boolean {
    try {
      const userAgent = navigator.userAgent || "";
      const isIOS = this.isIOSDevice();
      const isChrome = userAgent.includes("Chrome/") || userAgent.includes("CriOS/");
      return isIOS && isChrome;
    } catch {
      return false;
    }
  }

  private isQuotaExceededError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return (
        error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
        error.code === 22
      );
    }
    if (error instanceof Error) {
      return error.name === "QuotaExceededError" || error.message.toLowerCase().includes("quota");
    }
    return false;
  }

  private safeClearCleverTapStorage(): void {
    const clevertapKeys = [
      "WZRK_CAMP",
      "WZRK_PR",
      "WZRK_S",
      "WZRK_G",
      "WZRK_L",
      "WZRK_ARP",
      "WZRK_BB",
      "WZRK_NB",
      "WZRK_D",
      "WZRK_C",
      "ct_daily_date_tracker",
    ];
    try {
      // Also scan for any dynamic ct_* keys that accumulate unboundedly
      const dynamicCtKeys: string[] = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && (k.startsWith("WZRK_") || k.startsWith("ct_"))) {
            dynamicCtKeys.push(k);
          }
        }
      } catch {
        // ignore enumeration errors
      }
      [...clevertapKeys, ...dynamicCtKeys].forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore individual key removal errors
        }
      });
    } catch {
      // ignore
    }
  }

  /**
   * Installs a one-time monkey-patch on localStorage.setItem that intercepts
   * QuotaExceededError for WZRK_* / ct_* keys at runtime (i.e. after SDK init,
   * when CleverTap processes campaign responses via $WZRK_WR.tr).
   * On overflow: clear all CleverTap storage and retry the write once.
   * Fix for LOTTOSOCIAL-PWA-1N16.
   */
  private installStorageGuard(): void {
    if (ClevertapService._storageGuardInstalled) return;
    if (typeof window === "undefined" || !window.localStorage) return;

    ClevertapService._storageGuardInstalled = true;
    const original = localStorage.setItem.bind(localStorage);
    const self = this;

    localStorage.setItem = function (key: string, value: string): void {
      try {
        original(key, value);
      } catch (err) {
        if (
          self.isQuotaExceededError(err) &&
          (key.startsWith("WZRK_") || key.startsWith("ct_"))
        ) {
          // Storage is full — clear stale CleverTap data and retry once
          self.safeClearCleverTapStorage();
          try {
            original(key, value);
          } catch {
            // If it still fails after clearing, silently swallow — the SDK
            // will re-fetch campaign data on the next session.
          }
        } else {
          throw err;
        }
      }
    };
  }

  private isMainTabsRoute(): boolean {
    try {
      if (typeof window === "undefined") return false;
      const pathname = window.location.pathname;
      return pathname.includes("/mainTabs/") || pathname.includes("/mainTabs");
    } catch {
      return false;
    }
  }

  private shouldSkip(): boolean {
    if (!this.isCleverTapAvailable) return true;
    if (this.isTikTokBrowser()) return true;
    //Skip CleverTap on iOS to prevent findTopmostVisibleElement stack overflow
    if (this.isIOSDevice()) return true;
    // Skip CleverTap on UC Browser to prevent ucConfig ReferenceError (LOTTOSOCIAL-PWA-1N60)
    if (this.isUCBrowser()) return true;
    if (typeof window !== "undefined" && window.location.pathname.includes("/voucher")) return true;
    return false;
  }

  async init(): Promise<void> {
    try {
      // Skip CleverTap on iOS devices to prevent findTopmostVisibleElement stack overflow.
      // IMPORTANT: guard must run BEFORE the dynamic import so the SDK module is never
      // loaded on iOS — a static import would register DOM listeners immediately on module
      // evaluation, before any runtime guard could fire. (Fix for LOTTOSOCIAL-PWA-1M1F)
      if (this.isIOSDevice()) {
        this.isCleverTapAvailable = false;
        return;
      }

      if (this.isTikTokBrowser()) {
        this.isCleverTapAvailable = false;
        return;
      }

      // Skip CleverTap on UC Browser — its SDK references `ucConfig` (a UC Browser global)
      // without a safety guard, causing a ReferenceError when ucConfig is not injected.
      // (Fix for LOTTOSOCIAL-PWA-1N60)
      if (this.isUCBrowser()) {
        this.isCleverTapAvailable = false;
        return;
      }

      if (!environment.clevertapAccountId) {
        this.isCleverTapAvailable = false;
        return;
      }

      // Install a runtime localStorage.setItem guard BEFORE loading the SDK so that
      // any QuotaExceededError thrown by the SDK's S.save() (e.g. when processing
      // campaign responses via $WZRK_WR.tr after init) is caught, CleverTap storage
      // is cleared, and the write is retried. Fix for LOTTOSOCIAL-PWA-1N16.
      this.installStorageGuard();

      // If WZRK_CAMP is present and large (>200 KB), clear CleverTap cache before
      // loading the SDK to prevent QuotaExceededError on init. Threshold lowered from
      // 512 KB to 200 KB so storage is reclaimed before it becomes completely full.
      // The SDK re-fetches fresh campaign data from the network. Fix for LOTTOSOCIAL-PWA-1N16.
      try {
        const camp = localStorage.getItem("WZRK_CAMP");
        if (camp && camp.length > 200 * 1024) {
          this.safeClearCleverTapStorage();
        }
      } catch {
        // ignore - storage may already be inaccessible
      }

      // Dynamic import: SDK is only loaded for non-iOS browsers, preventing any
      // auto-initialised DOM traversal (findTopmostVisibleElement) from running on iOS.
      const { default: CleverTap } = await import("clevertap-web-sdk");
      this.ct = CleverTap;

      // Disable in-app messaging BEFORE init() so the SDK never registers
      // campaign-processing handlers (processCampaigns / showFooterNotification).
      // Setting this after init() is a no-op for already-registered response
      // handlers and is the root cause of LOTTOSOCIAL-PWA-1MY4 (innerHTML TypeError).
      this.ct.enableInApps = false;

      this.ct.init(environment.clevertapAccountId, "eu1");
      this.ct.spa = true;

      this.ct.privacy.push({ optOut: false });
      this.ct.privacy.push({ useIP: false });
      this.ct.setLogLevel(environment.production ? 0 : 2);

      this.isCleverTapAvailable = true;
    } catch (error) {
      // Catch any CleverTap initialization errors to prevent crashes
      this.isCleverTapAvailable = false;
      // If localStorage is full, clear CleverTap keys so future sessions can succeed.
      // Fix for LOTTOSOCIAL-PWA-1N16.
      if (this.isQuotaExceededError(error)) {
        this.safeClearCleverTapStorage();
      }
    }
  }

  setOffline(offline: boolean): void {
    if (!this.isCleverTapAvailable) return;
    try {
      this.ct?.setOffline(offline);
    } catch {
      return;
    }
  }

  public sendClevertapData(userId: string, user: Record<string, any>): void {
    if (this.shouldSkip()) return;
    try {
      const normalizedIdentity = this.normalizeString(userId);
      if (this.isInvalidValue(normalizedIdentity)) return;

      const normalizedEmail = this.normalizeEmail(user?.email);
      const normalizedPhone = this.normalizePhone(user?.msn);
      const signature = `${normalizedIdentity}|${normalizedEmail || ""}|${normalizedPhone || ""}`;
      if (signature === this.lastProfileSignature) return;

      const profilePayload = this.sanitizePayload({
        Identity: normalizedIdentity,
        username: normalizedIdentity,
        Name: `${user?.name || ""} ${user?.surname || ""}`.trim(),
        Email: normalizedEmail,
        Phone: normalizedPhone,
        pwa_user_id: normalizedIdentity,
      });
      if (!profilePayload?.Identity) return;

      this.ct?.onUserLogin.push({
        Site: profilePayload,
      });
      this.lastProfileSignature = signature;
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        this.safeClearCleverTapStorage();
        this.isCleverTapAvailable = false;
      }
    }
  }

  public checkRoutingModule(txtRout: string): void {
    if (!txtRout || typeof txtRout !== "string") return;
    if (txtRout.includes("/voucher") || txtRout.split("/").pop() === "voucher") return;
    if (this.shouldSkip()) return;

    let txtCategory: string;
    if (
      txtRout.split("/").pop() === "store" ||
      txtRout.split("/").pop() === "products" ||
      txtRout.split("/").pop() === "triggers" ||
      txtRout.split("/").pop() === "checkout" ||
      txtRout.split("/").pop() === "storePayment"
    ) {
      txtCategory = "Store";
    } else if (
      txtRout.split("/").pop() === "profile" ||
      txtRout.split("/").pop() === "your-details" ||
      txtRout.split("/").pop() === "gdpr" ||
      txtRout.split("/").pop() === "legal" ||
      txtRout.split("/").pop() === "wallet" ||
      txtRout.split("/").pop() === "account" ||
      txtRout.split("/").pop() === "signup" ||
      txtRout.split("/").pop() === "login" ||
      txtRout.split("/").pop() === "forgot-password" ||
      txtRout.split("/").pop() === "createAccount" ||
      txtRout.split("/").pop() === "addPayment" ||
      txtRout.split("/").pop() === "verifyPhoneNumber" ||
      txtRout.split("/").pop() === "completeRegistration" ||
      txtRout.split("/").pop() === "claimVoucher" ||
      txtRout.split("/").pop() === "jackpotTrigger" ||
      txtRout.split("/").pop() === "issueAward" ||
      txtRout.split("/").pop() === "registrationConfirmation"
    ) {
      txtCategory = "Profile";
    } else if (txtRout.split("/").pop() === "cashout" || txtRout.split("/").pop() === "cashoutStatus") {
      txtCategory = "Checkout";
    } else if (
      txtRout.includes("game") ||
      txtRout.split("/").pop() === "games" ||
      txtRout.split("/").pop() === "replayable"
    ) {
      txtCategory = "Games";
    } else if (
      txtRout.split("/").pop() === "syndicate" ||
      txtRout.split("/").pop() === "leaveSyndicate" ||
      txtRout.split("/").pop() === "checkedManagedTickets" ||
      txtRout.split("/").pop() === "invitationTicket" ||
      txtRout.split("/").pop() === "viewTicket" ||
      txtRout.split("/").pop() === "syndicateTicket" ||
      txtRout.split("/").pop() === "createSyndicateTicket" ||
      txtRout.split("/").pop() === "private-syndicate" ||
      txtRout.split("/").pop() === "checkWinnings" ||
      txtRout.split("/").pop() === "paymentConfirmation" ||
      txtRout.split("/").pop() === "setupCompleted" ||
      txtRout.split("/").pop() === "create-syndicate" ||
      txtRout.split("/").pop() === "syndicate-number" ||
      txtRout.split("/").pop() === "syndicate-checkout" ||
      txtRout.split("/").pop() === "syndicate-member"
    ) {
      txtCategory = "Member area";
    } else if (txtRout.split("/").pop() === "charity") {
      txtCategory = "Charity";
    } else if (
      txtRout.split("/").pop() === "voucher" ||
      txtRout.split("/").pop() === "home" ||
      txtRout.split("/").pop() === "fork1" ||
      txtRout.split("/").pop() === "fork2" ||
      txtRout.split("/").pop() === "helpSupport" ||
      txtRout.split("/").pop() === "welcome" ||
      txtRout.split("/").pop() === "mainTabs" ||
      txtRout.split("/").pop() === "checkWinningsSplash" ||
      txtRout.includes("breakdown")
    ) {
      txtCategory = "Blog";
    } else if (
      txtRout.split("/").pop() === "free-reg-step4b" ||
      txtRout.includes("free-reg-step4b") ||
      txtRout.includes("partial-registration") ||
      txtRout.split("/").pop() === "partial-registration"
    ) {
      txtCategory = "PartialReg";
    } else {
      txtCategory = "External";
    }

    if (txtCategory) {
      const pageName = txtRout.split("/").pop() || "";
      const txtRoutCapitalized = pageName.charAt(0).toUpperCase() + pageName.slice(1);
      const pageTitle = txtCategory === "PartialReg" ? txtRout?.split("/")[1]?.split("?")[0] : txtRoutCapitalized;
      this.passPageViewClevertap(
        pageTitle,
        txtCategory,
        window.location.pathname,
        document.referrer,
        window.location.search,
        document.title,
        window.location.href
      );
    }
  }

  public passPageViewClevertap(
    pageTitle: string,
    category: string,
    path: string,
    referrer: string,
    search: string,
    title: string,
    url: string
  ): void {
    if (this.shouldSkip()) return;
    if (url.includes("localhost:")) return;

    setTimeout(() => {
      try {
        const pageViewPayload = this.sanitizePayload({
          category,
          path,
          referrer,
          query: search,
          title,
          url,
        });
        this.ct?.event.push("Viewed " + pageTitle + " Page", pageViewPayload);
      } catch (error) {
        if (this.isQuotaExceededError(error)) {
          this.safeClearCleverTapStorage();
          this.isCleverTapAvailable = false;
        }
      }
    }, 2000);
  }

  public trackInlineNotifications(msg: string, body: Record<string, any>): void {
    if (this.shouldSkip()) return;
    try {
      this.ct?.event.push(msg, this.sanitizePayload({ ...body }));
    } catch (error) {
      // Silently fail to prevent app crashes from CleverTap errors
      return;
    }
  }

  public cancellationClickedCleverTapEvent(msg: string, body?: Record<string, any>): void {
    if (this.shouldSkip()) return;
    setTimeout(() => {
      try {
        this.ct?.event.push(msg, this.sanitizePayload({ ...body }));
      } catch (error) {
        // Silently fail to prevent app crashes from CleverTap errors
        return;
      }
    }, 2000);
  }
}
